
/* eslint-disable react/display-name */
/* eslint-disable max-len */
import { addMinutes, differenceInMinutes, format, startOfDay } from 'date-fns';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars';
import { usePopper } from 'react-popper';
import { useEffectOnce } from 'react-use';
import { PopperInner, PopperMount } from '../app/canvas/ui/PopperElements';
import { Flexer } from '../app/dashboard/elements/LayoutElements';
import { DropButton } from './DropButton';
import { Float } from './Float';
import { Menu } from './Menu';
import { usePrompt } from './Prompt';
import { remInPx } from './Utils';

const XScrollbars = Scrollbars as any;

export const TimePicker = React.memo<{ date: Date, onChange: (date: Date) => void }>(({ date, onChange }) => {

    const options = useMemo(() => {
        const start = startOfDay(new Date());
        const end = addMinutes(start, 24 * 60);
        const diff = differenceInMinutes(end, start);
        return Array.from(Array(diff / 15).keys()).map((i) => addMinutes(start, i * 15));
    }, [date]);

    const refs = options.map(() => useRef<HTMLDivElement>());

    const nearest = options.reduce((prev, curr) => {
        const prevDiff = differenceInMinutes(prev, date);
        const currDiff = differenceInMinutes(curr, date);
        return Math.abs(currDiff) < Math.abs(prevDiff) ? curr : prev;
    });

    useEffectOnce(() => {
        refs[options.indexOf(nearest)].current.scrollIntoView();
    });

    return <XScrollbars autoHeight autoHeightMax={remInPx(14.25)}>
        <Flexer width="8">
            <Menu>
                {options.map((d, i) =>
                    <Menu.Item ref={refs[i]} key={d.getTime()} onClick={() => onChange(d)}>
                        {format(d, 'h:mma').toLowerCase()}
                    </Menu.Item>
                )}
            </Menu>
        </Flexer>
    </XScrollbars>;
});

export const TimePopper = React.memo<{ date: Date, onChange: (date: Date) => void, disabled?: boolean }>(({ date, onChange, disabled }) => {

    const [open, setOpen] = useState(false);

    const [refElmt, setRefElmt] = useState(null);
    const [popElmt, setPopElmt] = useState(null);

    const { styles, attributes } = usePopper(refElmt, popElmt, { placement: 'bottom-start' });

    const promptRef = usePrompt({
        ignoreEnter: true,
        onAction: () => setOpen(false),
    });

    const onToggle = useCallback(() => setOpen(!open), [open, setOpen]);

    return <Flexer ref={promptRef} flex="row">
        <Flexer ref={setRefElmt}>
            <DropButton active={open} onClick={!disabled ? onToggle : null} disabled={disabled}>
                <span>{!!date ? format(date, 'h:mma').toLowerCase() : 'Not Set'}</span>
            </DropButton>
        </Flexer>
        {open && <PopperMount ref={setPopElmt} style={styles.popper} {...attributes.popper}>
            <PopperInner direction="down" offset=".5">
                <Float>
                    <TimePicker date={date} onChange={onChange} />
                </Float>
            </PopperInner>
        </PopperMount>
        }
    </Flexer >;
});
